import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, register, getCaptcha } from '../services/api'; // Import getCaptcha
import '../components/Auth.css';

const Auth = ({ language }) => {
    const [activeTab, setActiveTab] = useState('login');
    const [formData, setFormData] = useState({
        login: '',
        password: '',
        name: '',
        repeatPassword: '',
        captchaGuess: ''
    });
    const [captcha, setCaptcha] = useState({ question: '', token: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const navigate = useNavigate();

    // Load captcha on mount and tab change (only for register)
    const loadCaptcha = async () => {
        try {
            const res = await getCaptcha();
            setCaptcha(res.data);
        } catch (err) {
            console.error('Failed to load captcha', err);
        }
    };

    useEffect(() => {
        if (activeTab === 'register') {
            loadCaptcha();
        }
    }, [activeTab]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setError(null);
        setMessage(null);
        setFormData({
            login: '',
            password: '',
            name: '',
            repeatPassword: '',
            captchaGuess: ''
        });
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            if (activeTab === 'login') {
                const response = await login({
                    login: formData.login,
                    password: formData.password
                });

                if (response.data.success) {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    // Trigger custom event for Header sync
                    window.dispatchEvent(new Event('authChange'));
                    navigate('/');
                }
            } else {
                // Registration
                if (formData.password !== formData.repeatPassword) {
                    throw new Error('Пароли не совпадают');
                }

                const response = await register({
                    login: formData.login,
                    password: formData.password,
                    name: formData.name,
                    email: formData.login,
                    captchaToken: captcha.token,
                    captchaAnswer: formData.captchaGuess
                });

                if (response.data.success) {
                    setMessage('Регистрация успешна! Теперь вы можете войти.');
                    setActiveTab('login');
                    setFormData({ ...formData, password: '', repeatPassword: '' });
                }
            }
        } catch (err) {
            console.error('Auth error:', err);
            setError(err.response?.data?.error || err.message || 'Ошибка авторизации');
            // Refresh captcha on error
            if (activeTab === 'register') loadCaptcha();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-logo">
                    {/* Logo - assuming standard dark logo for light background */}
                    <img src="/local/templates/kapussta/img/logo.svg" alt="Kapussta" />
                </div>

                <div className="auth-tabs">
                    <button
                        className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                        onClick={() => handleTabChange('login')}
                    >
                        Вход
                    </button>
                    <button
                        className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
                        onClick={() => handleTabChange('register')}
                    >
                        Регистрация
                    </button>
                </div>

                <form className="auth-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            type="text"
                            name="login"
                            className="auth-input"
                            placeholder="Логин / Email"
                            value={formData.login}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {activeTab === 'register' && (
                        <div className="form-group">
                            <input
                                type="text"
                                name="name"
                                className="auth-input"
                                placeholder="Отображаемое имя"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <input
                            type="password"
                            name="password"
                            className="auth-input"
                            placeholder="Пароль"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {activeTab === 'register' && (
                        <>
                            <div className="form-group">
                                <input
                                    type="password"
                                    name="repeatPassword"
                                    className="auth-input"
                                    placeholder="Повторите пароль"
                                    value={formData.repeatPassword}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            {/* Captcha Section */}
                            <div className="form-group">
                                <div className="captcha-wrapper">
                                    <div className="captcha-display">
                                        {captcha.question || 'Loading...'}
                                        <span className="captcha-refresh" onClick={loadCaptcha} title="Обновить">↻</span>
                                    </div>
                                    <input
                                        type="text"
                                        name="captchaGuess"
                                        className="auth-input"
                                        placeholder="Ответ"
                                        value={formData.captchaGuess}
                                        onChange={handleChange}
                                        required
                                        style={{ width: '80px', textAlign: 'center' }}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {error && <div className="auth-error">{error}</div>}
                    {message && <div className="auth-success">{message}</div>}

                    <button type="submit" className="auth-button" disabled={loading}>
                        {loading ? 'Загрузка...' : (activeTab === 'login' ? 'Войти' : 'Зарегистрироваться')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Auth;
